import {
  createRepositoryLabelMutation,
  deleteLabelMutation,
  updateLabelMutation,
} from '@github-ui/server-persisted-queries'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import type {UpdateLabelMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelMutation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {LABELS} from '../constants/labels'
import {formatFormErrorMessage} from './format-form-error-message'
import type {LabelInput} from './label-connection-operations'

/**
 * The label writes, shared by both optimistic strategies. Keeping one copy per operation stops the
 * legacy and optimistic paths from drifting apart while the feature flag is rolling out.
 */

export async function createLabelRequest(
  repositoryNameWithOwner: string,
  repositoryId: string,
  input: LabelInput,
): Promise<CreateRepositoryLabelMutationMutation> {
  let response: Response
  try {
    response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/new`, {
      method: 'POST',
      body: {
        queryId: createRepositoryLabelMutation,
        repositoryId,
        name: input.name,
        description: input.description,
        color: input.color,
      },
    })
  } catch {
    throw new Error(LABELS.labelCreateError)
  }

  if (!response.ok) {
    throw new Error(LABELS.labelCreateError)
  }

  const result: {data: CreateRepositoryLabelMutationMutation} = await response.json()
  const errors = result.data.createLabel?.errors

  if (errors && errors.length > 0) {
    throw new Error(formatFormErrorMessage(errors))
  }

  if (!result.data.createLabel?.label) {
    throw new Error(LABELS.labelCreateError)
  }

  return result.data
}

export async function updateLabelRequest(
  repositoryNameWithOwner: string,
  labelId: string,
  input: LabelInput,
): Promise<UpdateLabelMutationMutation> {
  let response: Response
  try {
    response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}`, {
      method: 'PATCH',
      body: {
        queryId: updateLabelMutation,
        name: input.name,
        description: input.description,
        color: input.color,
      },
    })
  } catch {
    throw new Error(LABELS.editLabelError)
  }

  if (!response.ok) {
    throw new Error(LABELS.editLabelError)
  }

  const result: {data?: UpdateLabelMutationMutation | null; errors?: Array<{message: string}>} = await response.json()
  if (!result.data?.updateLabel?.label) {
    throw new Error(result.errors?.length ? formatFormErrorMessage(result.errors) : LABELS.editLabelError)
  }

  return result.data
}

export async function deleteLabelRequest(repositoryNameWithOwner: string, labelId: string): Promise<void> {
  let response: Response
  try {
    response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}?queryId=${deleteLabelMutation}`, {
      method: 'DELETE',
    })
  } catch {
    throw new Error('Delete failed')
  }

  if (!response.ok) {
    throw new Error('Delete failed')
  }
}

async function updateLabelArchiveState(
  repositoryNameWithOwner: string,
  labelId: string,
  action: 'archive' | 'unarchive',
  errorMessage: string,
): Promise<void> {
  let response: Response
  try {
    response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}/${action}`, {
      method: 'PATCH',
    })
  } catch {
    throw new Error(errorMessage)
  }

  if (!response.ok) {
    throw new Error(errorMessage)
  }

  const result = (await response.json()) as {errors?: unknown[]}
  if (result.errors?.length) {
    throw new Error(errorMessage)
  }
}

export function archiveLabelRequest(repositoryNameWithOwner: string, labelId: string): Promise<void> {
  return updateLabelArchiveState(repositoryNameWithOwner, labelId, 'archive', LABELS.archiveLabelError)
}

export function unarchiveLabelRequest(repositoryNameWithOwner: string, labelId: string): Promise<void> {
  return updateLabelArchiveState(repositoryNameWithOwner, labelId, 'unarchive', LABELS.unarchiveLabelError)
}
